import {
  BodyLink,
  Headline,
  LocationIcon,
  PhoneIcon,
  Subtitle
} from '@kvdbil/components';
import React from 'react';
import styled from 'styled-components';
import { AlignIcon } from '~/App/shared/components/Layout/AlignIcon';
import { useTranslation } from '~/Locale';
import { TableOfContentsType } from '../types';
import { getVehicleTypeTranslation } from '~/helpers/vehicleType';
import { getFacilityFilterIcon } from '../helpers';
import { FacilityWithCmsData } from '..';
import StructuredText from '~/App/components/DatoCMS/StructuredText';
import { FacilityData } from '~/App/shared/interfaces/store/CmsData';
import { Image } from 'react-datocms';

const List = styled.ul`
  display: grid;
  grid-gap: 1rem 1rem;
`;

const StyledHeadline = styled(Headline)`
  margin: 2rem 0;
`;

const MapLink = styled(BodyLink)`
  text-transform: uppercase;
`;

const Heading = styled(Subtitle)`
  margin-bottom: 0.5rem;
`;

const HandlesItem = styled.li`
  display: flex;
  column-gap: 0.5rem;
  align-items: center;
  padding: 0.5rem 0;
  color: ${({ theme }) => theme.colors.text.disabled};
  border-bottom: 1px solid ${({ theme }) => theme.colors.grayscaleToned.light3};
`;

const ContactItem = styled.li`
  display: flex;
  column-gap: 0.5rem;
`;

type Props = {
  cmsData: FacilityData;
  facility: FacilityWithCmsData;
  tableOfContents: TableOfContentsType;
};

function FacilityContent({ cmsData, facility, tableOfContents }: Props) {
  const { t } = useTranslation();

  const googleMapsQuery = encodeURIComponent(
    `${cmsData?.streetAddress} ${cmsData?.city} ${cmsData?.zipCode}`
  );
  const generatedMapsUrl = `https://maps.google.com/?q=${googleMapsQuery}`;
  const mapsUrl = cmsData?.customMapLink || generatedMapsUrl;

  return (
    <List>
      <li>
        {cmsData?.image?.responsiveImage && (
          <Image
            data={cmsData?.image?.responsiveImage}
            style={{ maxWidth: '100%' }}
          />
        )}
        <div>
          <StyledHeadline as="h2">{cmsData?.title}</StyledHeadline>
          {mapsUrl && (
            <MapLink
              as="a"
              href={mapsUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              <AlignIcon right={0.5}>
                <LocationIcon />
                {t('Show in maps')}
              </AlignIcon>
            </MapLink>
          )}
        </div>
      </li>
      <li id="facilityHandles">
        <Heading as="h3">{t('The facility handles')}</Heading>
        <ul>
          {facility.objectTypes.map(type => (
            <HandlesItem key={type}>
              {getVehicleTypeTranslation(type, t)}
              {getFacilityFilterIcon(type)}
            </HandlesItem>
          ))}
        </ul>
      </li>
      {tableOfContents.map(content => {
        if (!content.wysiwygBody) return;

        const structuredTextData = cmsData?.[content.wysiwygBody];
        if (!structuredTextData) return;

        return (
          <li id={content.id} key={content.id}>
            <Heading as="h3">{content.text}</Heading>
            <StructuredText data={structuredTextData} />
          </li>
        );
      })}

      {(cmsData?.phone || cmsData?.fax) && (
        <li id="contact">
          <Heading as="h3">{t('Contact')}</Heading>
          <ul>
            {cmsData?.phone && (
              <ContactItem>
                {t('Telephone')}
                <a href={`tel:${cmsData?.phone}`}>
                  <PhoneIcon />
                  {cmsData?.phone}
                </a>
              </ContactItem>
            )}
            {cmsData?.fax && (
              <ContactItem>
                <span>{t('Fax')}</span>
                <span>{cmsData?.fax}</span>
              </ContactItem>
            )}
          </ul>
        </li>
      )}

      <li id="address">
        <Heading as="h3">{t('Address')}</Heading>
        <div>
          {cmsData?.streetAddress}
          <br />
          {cmsData?.zipCode} {cmsData?.city}
        </div>
      </li>

      {cmsData?.directions && (
        <li id="findUs">
          <Heading as="h3">{t('Find us')}</Heading>
          <StructuredText data={cmsData?.directions} />
        </li>
      )}
    </List>
  );
}

export default FacilityContent;
